import { useState } from "react";
import { usePublicConfig } from "@/lib/publicConfig";
import { sellerNet } from "@/lib/sellerNet";
import { CONDITIONS, condDesc } from "@/lib/conditions";
import { isSealedSel, photoCount } from "@/lib/publishQueue";
import { OVERFRAME_RE, ygoRarityLabel } from "@/lib/rarityTier";
import { Mono, StepHead, Panel, Chip, Field, Stepper, BackPill, Primary, Ghost, PhotoTiles, PhotoCount, PhotoHint, LocalOnly, imgUrl, money, decimal, integer } from "./ui";

export const LANGS = [{ key: "es", label: "ES" }, { key: "en", label: "EN" }, { key: "ja", label: "JA" }, { key: "otro", label: "Otro" }];
const HOUSES = ["PSA", "BGS", "CGC", "SGC"];
const YGO_RARITIES = ["Common", "Rare", "Super Rare", "Ultra Rare", "Secret Rare", "Ultimate Rare", "Ghost Rare", "Prismatic Secret Rare",
  "Starlight Rare", "Starlight Rare (color)", "Quarter Century Secret Rare", "Platinum Secret Rare", "Extra Secret Rare", "Collector's Rare", "Gold Rare", "Platinum Rare"];

export const NetCards = ({ price, quantity, ship, compact }) => {
  const cfg = usePublicConfig();
  if (!cfg.commission_tiers) return null;
  const line = [{ price: Math.max(0, parseFloat(price) || 0), quantity }];
  const pos = (n) => Math.max(0, n);
  const card = (method) => {
    const b = sellerNet(line, 0, method, cfg);
    const sh = ship ? ship.map((s) => sellerNet(line, s, method, cfg).net - b.net) : null;
    const t = method === "transfer";
    return (
      <div key={method} className={`rounded-[12px] border ${compact ? "px-3 py-2 flex items-center justify-between" : "p-3"}`}
        style={t ? { borderColor: "rgba(52,211,153,.45)", background: "rgba(52,211,153,.06)" } : { borderColor: "rgba(255,255,255,.1)" }} data-testid={`p3-net-${method}`}>
        <div className="text-[11px] font-semibold text-white/80">{t ? "Transferencia" : compact ? "Payphone" : "Tarjeta · Payphone"}</div>
        <div className={`font-extrabold ${compact ? "text-[16px]" : "text-[22px]"}`} style={{ color: t ? "#34D399" : "#fff" }} data-testid={`p3-net-${method}-amount`}>{money(pos(b.net))}</div>
        {!compact && <div className="sx-mono text-[8.5px] text-white/45 leading-relaxed mt-0.5">−{money(b.commission)} HIT ({b.pct}%){t ? "" : ` −${money(b.payphone)} PP`} · {sh ? `+ envío ${money(sh[0])}${sh[1] !== sh[0] ? `–${money(sh[1])}` : ""}` : "+ envío"}</div>}
      </div>
    );
  };
  return <div className="grid grid-cols-2 gap-2">{card("transfer")}{card("card")}</div>;
};

const shipText = (cfg) => {
  if (!cfg) return "";
  if (cfg.mode === "meetup_only") return "Solo entrega en persona";
  if (cfg.mode === "detailed") { const r = Object.values(cfg.per_province_rates || {}).map(Number); return r.length ? `Según provincia ${money(Math.min(...r))}–${money(Math.max(...r))}` : ""; }
  return `Provincia ${money(cfg.same_province_rate)} · Resto ${money(cfg.other_province_rate)}`;
};

const ShipBlock = ({ ctx, form, set, onShipEdit }) => {
  const [open, setOpen] = useState(false);
  if (!ctx.hasRates) {
    return <button type="button" onClick={onShipEdit} className="w-full text-left p-3 rounded-[12px] border border-[#fbbf24]/50 text-[13px] text-[#fbbf24]" data-testid="p3-configure-ship">Configura tu envío para publicar →</button>;
  }
  return (
    <Panel className="p-3" data-testid="p3-ship">
      <div className="flex items-center justify-between gap-2 text-[12.5px]">
        <span className="min-w-0"><b className="text-white">Envío del perfil</b><span className="text-white/55"> · {shipText(ctx.shipping)}</span></span>
        <button type="button" onClick={() => setOpen(!open)} className="text-[#FF6B5E] font-bold shrink-0" data-testid="p3-ship-change">Cambiar</button>
      </div>
      <div className="mt-2.5"><LocalOnly ctx={ctx} on={form.local_only} onChange={(v) => set({ local_only: v })} testid="p3-local-only" /></div>
      {open && (
        <div className="mt-3 space-y-2" data-testid="p3-ship-extra">
          <Mono>Envío gratis desde (opcional)</Mono>
          <div className="grid grid-cols-2 gap-2">
            <Field type="text" inputMode="numeric" placeholder="N cartas" value={form.free_ship_min_items} onChange={(e) => set({ free_ship_min_items: integer(e.target.value) })} data-testid="p3-free-items" />
            <Field type="text" inputMode="decimal" placeholder="$ monto" value={form.free_ship_min_amount} onChange={(e) => set({ free_ship_min_amount: decimal(e.target.value) })} data-testid="p3-free-amount" />
          </div>
          <button type="button" onClick={onShipEdit} className="text-[12px] text-white/70 underline" data-testid="p3-ship-edit">Editar tarifas del perfil</button>
        </div>
      )}
    </Panel>
  );
};

const Printing = ({ sel, form, set }) => {
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(!!form.yugioh_rarity_note);
  const list = sel.all_printings || [];
  if (!list.length) return null;
  const cur = form.yugioh_set_code ? `${form.yugioh_set_code} · ${form.yugioh_rarity}` : "Elige la impresión";
  return (
    <div data-testid="p3-printing">
      <div className="flex items-center gap-2">
        <span className="h-7 px-2.5 rounded-full border border-[#FF4438]/60 sx-mono text-[10px] text-white grid place-items-center" data-testid="p3-printing-chip">{cur}</span>
        {list.length > 1 && <button type="button" onClick={() => setOpen(!open)} className="text-[12px] text-[#FF6B5E] font-bold" data-testid="p3-printing-change">Cambiar</button>}
      </div>
      {open && (
        <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
          {list.map((p) => (
            <button key={`${p.set_code}-${p.rarity}`} type="button" onClick={() => { set({ yugioh_set_code: p.set_code, yugioh_rarity: p.rarity, version_label: `${p.set_code} · ${p.rarity}`, printing_set_name: p.set_name || "", yugioh_rarity_note: OVERFRAME_RE.test(p.set_code || "") ? "Overframe" : "" }); setOpen(false); }}
              className="w-full text-left px-3 py-2 rounded-[10px] border border-white/10 text-[12px] text-white/80 hover:border-[#FF4438]" data-testid={`p3-printing-${p.set_code}`}>{p.set_code} · {p.rarity}{p.set_name ? ` · ${p.set_name}` : ""}</button>
          ))}
        </div>
      )}
      {form.yugioh_set_code && (
        <details className="mt-2" data-testid="p3-rarity-other" open={detailOpen} onToggle={(e) => setDetailOpen(e.currentTarget.open)}>
          <summary className="text-[12px] text-white/60 cursor-pointer">¿Tu carta tiene otra rareza o un detalle (arte extendido, color de Starlight)?</summary>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <select value={form.yugioh_rarity} onChange={(e) => set({ yugioh_rarity: e.target.value, version_label: `${form.yugioh_set_code} · ${e.target.value}` })}
              className="h-12 rounded-[12px] bg-[#171717] border border-white/10 px-3 text-[13px] text-white" data-testid="p3-rarity-select">
              {[...new Set([form.yugioh_rarity, ...YGO_RARITIES].filter(Boolean))].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <Field placeholder="Detalle (ej. Starlight azul)" value={form.yugioh_rarity_note || ""} onChange={(e) => set({ yugioh_rarity_note: e.target.value.slice(0, 60) })} data-testid="p3-rarity-note" />
          </div>
          {OVERFRAME_RE.test(form.yugioh_set_code || "") ? (
            <div className="mt-2 text-[11px] text-white/60" data-testid="p3-overframe-auto">Esta impresión es Overframe (arte que sale del marco).</div>
          ) : <button type="button" onClick={() => set({ yugioh_rarity_note: form.yugioh_rarity_note === "Overframe" ? "" : "Overframe" })}
            className={`mt-2 h-8 px-3 rounded-full border text-[11px] ${form.yugioh_rarity_note === "Overframe" ? "border-[#FF4438] text-white bg-[#FF4438]/15" : "border-white/15 text-white/70"}`} data-testid="p3-overframe-toggle">
            Overframe (arte que sale del marco)
          </button>}
          <div className="text-[11px] text-white/45 mt-1">Si no figura en el catálogo, pasa a revisión y el comprador ve la rareza y el detalle.</div>
        </details>
      )}
    </div>
  );
};

const Head = ({ d }) => {
  const s = d.selected;
  const label = d.form.yugioh_set_code ? `${d.form.yugioh_set_code} · ${ygoRarityLabel(d.form)}` : d.form.version_label || (d.form.variant === "reverse_holo" ? "Reverse Holo" : s.holo_card ? "Holo" : isSealedSel(s) ? "Sellado" : "Normal");
  return (
    <Panel className="flex gap-3 p-3" data-testid="p3-card">
      <div className="w-11 h-[60px] rounded-md overflow-hidden bg-black shrink-0 grid place-items-center">{imgUrl(s.image_url) ? <img src={imgUrl(s.image_url)} alt={s.name} className="w-full h-full object-contain" /> : <span className="text-[8px] text-white/30">Sin imagen</span>}</div>
      <div className="min-w-0">
        <div className="text-[18px] font-extrabold text-white leading-tight" data-testid="p3-card-name">{s.name}</div>
        <div className="text-[12px] text-white/55 truncate" data-testid="p3-card-set">{[d.form.printing_set_name || s.set_name, s.local_id && `#${s.local_id}`].filter(Boolean).join(" · ")}</div>
        <span className="inline-block mt-1.5 sx-mono text-[9px] uppercase px-1.5 py-0.5 rounded border border-[#FF4438]/60 text-[#FF6B5E]" data-testid="p3-version-chip">{label}</span>
      </div>
    </Panel>
  );
};

// P3 · Ficha rápida · todo prellenado
export const P3Ficha = ({ d, setForm, setPhotos, game, ctx, onBack, onAddQueue, onPublishNow, publishing, queueCount, onQueue, onShipEdit, hasPayout, onPayout, photoDrop, formRef, compact }) => {
  const f = d.form; const s = d.selected; const sealed = isSealedSel(s);
  const qty = Math.max(1, parseInt(f.quantity, 10) || 1);
  const n = photoCount(d);
  return (
    <div ref={formRef} className="space-y-4 pb-24 xl:pb-0" data-testid="p3-ficha">
      {!compact && <div className="flex items-center justify-between gap-3"><BackPill onClick={onBack} data-testid="p3-back">← Cambiar carta</BackPill><div className="shrink-0"><StepHead step={2} label="" /></div></div>}
      <Head d={d} />
      {game === "yugioh" && !sealed && <Printing sel={s} form={f} set={setForm} />}
      {s.first_edition_available && (
        <label className="flex items-center gap-2 text-[12.5px] text-[#fbbf24]" data-testid="p3-first-edition">
          <input type="checkbox" checked={!!f.first_edition} onChange={(e) => setForm({ first_edition: e.target.checked })} className="accent-[#fbbf24]" />¿Es 1.ª edición? · pasa a revisión
        </label>
      )}
      <div>
        <div className="flex justify-between mb-2"><Mono>Idioma</Mono><span className="text-[10.5px] text-white/40">Recordado de tu última carga</span></div>
        <div className="grid grid-cols-4 gap-2">{LANGS.map((l) => <Chip key={l.key} on={f.language === l.key} onClick={() => setForm({ language: l.key })} data-testid={`p3-lang-${l.key}`}>{l.label}</Chip>)}</div>
        {f.language === "otro" && <Field className="mt-2" placeholder="¿Qué idioma? (ej. Coreano)" value={f.language_other} onChange={(e) => setForm({ language_other: e.target.value.slice(0, 30) })} data-testid="p3-lang-other" />}
      </div>
      {!sealed && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <Mono>Condición</Mono>
            <label className="flex items-center gap-1.5 text-[11px] text-white/60"><input type="checkbox" checked={!!f.graded} onChange={(e) => setForm({ graded: e.target.checked })} data-testid="p3-graded" />Gradeada · PSA BGS CGC SGC</label>
          </div>
          {f.graded ? (
            <div className="space-y-2" data-testid="p3-graded-fields">
              <div className="grid grid-cols-4 gap-2">{HOUSES.map((h) => <Chip key={h} on={(f.graded_house || "PSA") === h} onClick={() => setForm({ graded_house: h })} data-testid={`p3-house-${h}`}>{h}</Chip>)}</div>
              <Field placeholder="Calificación (ej. 10, 9.5)" value={f.graded_grade || ""} onChange={(e) => setForm({ graded_grade: e.target.value.slice(0, 6) })} data-testid="p3-grade" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-5 gap-1.5">{CONDITIONS.map((c) => <Chip key={c.value} on={f.condition === c.value} onClick={() => setForm({ condition: c.value })} className="sx-mono text-[11px]" data-testid={`p3-cond-${c.value}`}>{c.value}</Chip>)}</div>
              <div className="text-[11.5px] text-white/55 mt-1.5" data-testid="p3-cond-desc">{CONDITIONS.find((c) => c.value === f.condition)?.name} · {condDesc(f.condition)}</div>
            </>
          )}
        </div>
      )}
      <div className="grid grid-cols-[minmax(0,1fr)_112px] gap-2">
        <div className="min-w-0"><Mono className="mb-2">Precio · USD</Mono>
          <div className="h-12 rounded-[12px] border border-[#FF4438] flex items-center px-3 gap-1.5"><span className="text-white/50">$</span>
            <input type="text" inputMode="decimal" value={f.price} onChange={(e) => setForm({ price: decimal(e.target.value) })} placeholder="0.00" className="flex-1 min-w-0 bg-transparent text-[22px] font-extrabold text-white focus:outline-none" data-testid="p3-price" />
          </div>
        </div>
        <div><Mono className="mb-2">Cantidad</Mono><Stepper value={qty} onChange={(v) => setForm({ quantity: v })} testid="p3-qty" /></div>
      </div>
      {qty > 1 && (
        <label className="flex items-start gap-2 text-[12px] text-white/75" data-testid="p3-same-condition">
          <input type="checkbox" checked={!!f.bulk_disclaimer_accepted} onChange={(e) => setForm({ bulk_disclaimer_accepted: e.target.checked })} className="mt-0.5" />Todas en la misma condición
        </label>
      )}
      <div>
        <div className="flex justify-between mb-2"><Mono>Lo que recibes</Mono><span className="text-[10.5px] text-white/40">Envío según destino del comprador</span></div>
        <NetCards price={f.price} quantity={qty} ship={ctx.shipRange} compact={compact} />
      </div>
      <div>
        <PhotoCount n={n} />
        <PhotoTiles paths={d.photo_paths || []} files={d.photos} dropProps={photoDrop?.dropProps} dragging={photoDrop?.dragging}
          onAdd={(fs) => setPhotos([...d.photos, ...fs].slice(0, 12))}
          onRemove={(i) => { const k = (d.photo_paths || []).length; if (i < k) setPhotos(d.photos, (d.photo_paths || []).filter((_, j) => j !== i)); else setPhotos(d.photos.filter((_, j) => j !== i - k)); }} testid="p3-photos" />
        {compact ? <div className="text-[11px] text-[#FF6B5E] mt-1.5">Arrastra 2 fotos aquí o pégalas (Ctrl+V)</div> : <PhotoHint />}
      </div>
      <ShipBlock ctx={ctx} form={f} set={setForm} onShipEdit={onShipEdit} />
      {d.error && <div className="text-[12px] text-[#FF6B5E]" data-testid="p3-error">No se pudo publicar: {d.error}</div>}
      <div className="grid grid-cols-2 gap-2">
        <Ghost onClick={onAddQueue} disabled={publishing} data-testid="p3-add-queue">+ Añadir a la cola<span className="block text-[10px] font-normal text-white/50">{compact ? "ENTER" : "y buscar otra"}</span></Ghost>
        {hasPayout
          ? <Primary onClick={onPublishNow} loading={publishing} loadingText="Publicando…" data-testid="p3-publish-now">Publicar ahora</Primary>
          : <Primary onClick={onPayout} data-testid="p3-add-payout">Agrega datos de cobro</Primary>}
      </div>
      {queueCount > 0 && !compact && (
        <div className="fixed bottom-0 inset-x-0 z-30 p-3 bg-[#080808]/95 backdrop-blur border-t border-white/10 xl:hidden">
          <button type="button" onClick={onQueue} className="w-full h-11 rounded-full border border-white/20 text-white text-[13px] font-bold" data-testid="p3-queue-bar">Cola · {queueCount} · Ver</button>
        </div>
      )}
    </div>
  );
};
